/**
 * The Pro cover (design_handoff_pro_page/README.md "Cover"): the event cover's geometry — the
 * same `EventCover` the event page draws, so a layout can never disagree between the two —
 * with the Pro identity in the text column and the cover video riding photo 1's tile.
 *
 *   desktop  radius 28, 540px (≥ 1100 wide) else 500; text column 36/40 with the logo, kind
 *            pill + label, name, craft chips, trust line, bio, place line, MANAGED BY at the foot.
 *   phone    photos only, 260px radius 22; the identity panel is rendered by the page below it.
 *
 * `editing` turns every tile into a drag target and shows the poster photo instead of the
 * video (so a drag under it still reaches the photo); `video` resumes when the editor closes.
 */
import React from 'react';
import EventCover from '../event/EventCover';
import ProCoverVideo from './ProCoverVideo';
import { focalPosition } from '../event/coverLayout';
import { RING_ON } from '../surface/SurfaceStyleContext';
import { HOT_PINK, MUTED, FAINT, KIND_LABEL, trustLine, trustColor } from './proPageModel';
import { craftLine, isVendorKind } from './vendor/vendorUi';
import { craftLabel } from '../../utils/proCrafts';

const heightFor = (W, layout, mobile) => (mobile ? 260 : W >= 1100 ? 540 : 500);
const label = (size, spacing = '0.16em') => ({ fontSize: size, fontWeight: 700, letterSpacing: spacing, textTransform: 'uppercase' });

export function Logo({ biz, size, radius, fontSize }) {
  const listed = !!biz.isListed;
  const logo = !listed ? biz.logoUrl : null;
  return (
    <span className="flex flex-none items-center justify-center overflow-hidden" style={{ width: size, height: size, borderRadius: radius, background: listed || !logo ? '#F2F2F2' : '#fff', fontSize, fontWeight: 800, color: FAINT }} aria-hidden={!logo}>
      {logo ? <img src={logo} alt="" className="block h-full w-full object-contain" /> : (biz.name || '?').trim().charAt(0).toUpperCase()}
    </span>
  );
}

export function KindPills({ biz, small = false }) {
  const listed = !!biz.isListed;
  return (
    <span className="flex flex-wrap items-center" style={{ gap: small ? 8 : 10 }}>
      <span className="rounded-full" style={{ padding: small ? '4px 10px' : '5px 12px', background: listed ? '#E5E7EB' : HOT_PINK, color: listed ? '#000' : '#fff', fontSize: small ? 9 : 10, fontWeight: 800, letterSpacing: small ? '0.14em' : '0.16em' }} data-testid={listed ? 'pill-listed' : 'pill-pro'}>{listed ? 'LISTED' : 'PRO'}</span>
      <span style={{ ...label(small ? 9 : 11, small ? '0.16em' : '0.18em'), color: MUTED }}>{listed && (biz.kind || 'bookable') === 'bookable' ? 'BOOKABLE' : KIND_LABEL[biz.kind || 'bookable']}</span>
    </span>
  );
}

export function TrustLine({ biz, size = 12 }) {
  const t = trustLine(biz);
  return (
    <span className="flex flex-wrap" style={{ gap: '6px 14px', fontSize: size, letterSpacing: '0.06em' }} data-testid="trust-line">
      <span className="uppercase" style={{ fontWeight: 700, color: trustColor(t.tone) }}>{t.text}</span>
      {t.note && <span className="uppercase" style={{ color: MUTED }}>{t.note}</span>}
      {!biz.isListed && biz.isCharity && biz.charityVerified && <span className="uppercase" style={{ fontWeight: 700, color: HOT_PINK }}>PARTY FOR GOOD</span>}
    </span>
  );
}

export function Bio({ biz, phone = false }) {
  if (!biz.bio) return null;
  const italic = !biz.isListed && !isVendorKind(biz.kind);
  return (
    <p style={{ margin: 0, fontFamily: italic ? '"Times New Roman", Times, serif' : 'inherit', fontStyle: italic ? 'italic' : 'normal', fontSize: italic ? (phone ? 18 : 24) : (phone ? 14 : 16), lineHeight: phone ? 1.45 : 1.4, maxWidth: 620, textWrap: 'pretty' }} data-testid="pro-bio">{biz.bio}</p>
  );
}

export function craftChips(biz) {
  const line = craftLine(biz);
  // The kind already sits beside the pill (EATERY · BAR · …), so it is not repeated as a chip.
  if (line) return line.split(' · ').filter((c, i) => !(i === 0 && c === (biz.kind || '').toUpperCase()));
  return (biz.craftLabels?.length ? biz.craftLabels : (biz.crafts || []).map(craftLabel)).slice(0, 4).map((c) => String(c).toUpperCase());
}

export function PlaceLine({ biz, size = 12, onOutbound }) {
  const city = biz.baseCity || biz.listedCity || '';
  const href = biz.websiteUrl || biz.listedWebsite || '';
  const site = href.replace(/^https?:\/\//, '').replace(/\/$/, '');
  if (!city && !site) return null;
  return (
    <span className="flex flex-wrap" style={{ gap: '8px 18px', fontSize: size, letterSpacing: '0.05em' }}>
      {city && <span className="uppercase" style={{ color: MUTED }}>{city}</span>}
      {site && <a href={href} target="_blank" rel="noreferrer" onClick={() => onOutbound?.('website')} className="underline" style={{ color: '#000' }}>{site}</a>}
    </span>
  );
}

function EditButton({ open, onClick, phone }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={open} data-testid="edit-cover"
      className="absolute flex items-center rounded-full font-mono uppercase"
      style={{ right: phone ? 10 : 16, top: phone ? 10 : 16, zIndex: 3, gap: 8, height: phone ? 36 : 40, padding: phone ? '0 14px' : '0 16px', border: '2px solid transparent', boxSizing: 'border-box', background: open ? RING_ON : '#fff', color: open ? '#fff' : '#000', fontSize: phone ? 9 : 10, fontWeight: 800, letterSpacing: '0.14em', cursor: 'pointer' }}>
      {!phone && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" aria-hidden="true"><path d="M3 5h18v14H3zM3 15l5-5 4 4 3-3 6 6" /></svg>}
      {open ? 'EDITING COVER' : 'EDIT COVER'}
    </button>
  );
}

export default function ProCover({ biz, photos, layout, split, video, phone = false, owner = false, editing = false, onToggleEdit, onFocal, onOpenPhoto, onRatio, onOwner, onOutbound }) {
  const listed = !!biz.isListed;
  const hasPhotos = photos.length > 0;
  const renderMedia = (photo, i, pos) => {
    if (i !== 0 || !video || editing) return null;
    return <ProCoverVideo photoUrl={photo.src} video={video} objectPosition={pos} />;
  };
  const common = { photos, layout, split, editable: editing, onFocal, onOpenPhoto: editing ? undefined : onOpenPhoto, onRatio, renderMedia, heightFor, testId: 'pro-cover' };

  if (phone) {
    return (
      <div className="relative overflow-hidden bg-white" style={{ borderRadius: 22, height: 260 }} data-testid="pro-cover-mobile" data-layout={hasPhotos ? undefined : 'none'}>
        {hasPhotos
          ? <EventCover {...common} mobile />
          : <div className="flex h-full items-center justify-center" style={{ background: '#F2F2F2', ...label(10), color: FAINT }}>NO PHOTOS YET</div>}
        {owner && <EditButton open={editing} onClick={onToggleEdit} phone />}
      </div>
    );
  }

  const canOwner = !listed && !biz.hideOwnerOnCover && biz.ownerHandle;
  return (
    <div className="relative" data-testid={listed ? 'pro-header-listed' : 'pro-header'}>
      <EventCover {...common} className="rounded-[28px]" textPadding="36px 40px">
        {({ textPx }) => {
          const room = textPx - 154;
          const titleFs = room > 520 ? 46 : room > 360 ? 38 : 30;
          const chips = craftChips(biz);
          return (
            <>
              <div className="flex items-center" style={{ gap: 22 }}>
                <Logo biz={biz} size={132} radius={24} fontSize={56} />
                <span className="flex min-w-0 flex-col" style={{ gap: 12 }}>
                  <KindPills biz={biz} />
                  <h1 className="m-0 font-mono" style={{ fontSize: titleFs, fontWeight: 700, lineHeight: 1.05, letterSpacing: '-0.01em', textWrap: 'pretty', overflowWrap: 'anywhere', textTransform: 'none' }}>{biz.name}</h1>
                </span>
              </div>
              {chips.length > 0 && (
                <span className="flex flex-wrap" style={{ gap: 6 }}>
                  {chips.map((c) => <span key={c} className="rounded-full" style={{ padding: '7px 12px', background: '#F2F2F2', ...label(10, '0.14em') }}>{c}</span>)}
                </span>
              )}
              <TrustLine biz={biz} />
              <Bio biz={biz} />
              {(isVendorKind(biz.kind) || listed) && <PlaceLine biz={biz} onOutbound={onOutbound} />}
              {canOwner && (
                <span className="flex items-center" style={{ marginTop: 'auto', gap: 12 }}>
                  <button type="button" onClick={onOwner} className="flex flex-none items-center justify-center overflow-hidden rounded-full font-mono font-bold" style={{ width: 48, height: 48, background: '#F2F2F2' }} aria-label={`${biz.ownerName || biz.ownerHandle}'s Portal Page`}>
                    {biz.ownerAvatarUrl ? <img src={biz.ownerAvatarUrl} alt="" className="block h-full w-full object-cover" /> : (biz.ownerName || '?').charAt(0).toUpperCase()}
                  </button>
                  <span className="flex flex-col" style={{ gap: 3 }}>
                    <span style={{ ...label(9), color: MUTED }}>MANAGED BY</span>
                    <span style={{ fontSize: 14, fontWeight: 700 }}>{biz.ownerName || `@${biz.ownerHandle}`} <span style={{ fontWeight: 400, color: MUTED }}>@{biz.ownerHandle}</span></span>
                  </span>
                  <button type="button" onClick={onOwner} className="font-mono" style={{ marginLeft: 8, ...label(10, '0.14em'), color: MUTED, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }} data-testid="view-resume">VIEW PORTAL PAGE →</button>
                </span>
              )}
            </>
          );
        }}
      </EventCover>
      {!hasPhotos && (
        <div className="absolute flex flex-col items-center justify-center text-center" style={{ right: 32, top: 32, bottom: 32, width: '34%', borderRadius: 22, background: '#F5F5F5', gap: 8, padding: 24, boxSizing: 'border-box' }} data-testid="no-photos-strip">
          <span style={{ ...label(12), color: FAINT }}>NO PHOTOS</span>
          <span style={{ fontSize: 12, lineHeight: 1.6, color: MUTED }}>{listed ? 'Listed from public information. The business adds its work when it claims the page.' : 'Add photos in EDIT COVER and they show here.'}</span>
        </div>
      )}
      {owner && <EditButton open={editing} onClick={onToggleEdit} />}
    </div>
  );
}

export { focalPosition };
